import React, { useState, useEffect, useMemo } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { useUser } from '../../contexts/UserContext';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Grid,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Close,
  Computer,
  Delete as DeleteIcon,
  Add as AddIcon,
  Sort as SortIcon
} from '@mui/icons-material';
import { getNodes, transformNodeToDisplayFormat } from '../api/services/computeService/NodeService';
import {
  assignNodeToClient,
  removeNodeFromClient,
  getClientNodes
} from '../api/services/computeService/ClientNodeService';
import {
  assignNodeToDepartments,
  removeNodeFromDepartment,
  getDepartmentNodes
} from '../api/services/computeService/DepartmentNodeService';
import {
  assignNodeToTeams,
  removeNodeFromTeam,
  getTeamNodes
} from '../api/services/computeService/TeamNodeService';
import {
  assignNodeToUsers,
  removeNodeFromUser,
  getUserNodes
} from '../api/services/computeService/UserNodeService';

// Helper function to format GPU information
const formatGpuInfo = (node) => {
  if (node.gpuBreakdown && node.gpuBreakdown.length > 0) {
    return node.gpuBreakdown.map(gpu => {
      // Extract vendor from GPU model
      let vendor = 'Unknown';
      const model = gpu.model.toLowerCase();
      if (model.includes('rtx') || model.includes('gtx') || model.includes('tesla') || model.includes('quadro') || model.includes('geforce')) {
        vendor = 'NVIDIA';
      } else if (model.includes('radeon') || model.includes('rx ')) {
        vendor = 'AMD';
      } else if (model.includes('intel') || model.includes('arc')) {
        vendor = 'Intel';
      }

      return `${gpu.count}x ${vendor} ${gpu.model}`;
    }).join(', ');
  } else if (node.numGpus && node.numGpus > 0) {
    return `${node.numGpus} GPU${node.numGpus !== 1 ? 's' : ''}`;
  }
  return '';
};

const NodeAssignmentDialog = ({
  open,
  onClose,
  entityType,
  entityId,
  entityName,
  onSuccess
}) => {
  const { keycloak } = useKeycloak();
  const { userPermissions, isSuperAdmin } = useUser();
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [availableNodes, setAvailableNodes] = useState([]);
  const [assignedNodeIds, setAssignedNodeIds] = useState([]);
  const [selectedNodes, setSelectedNodes] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sortBy, setSortBy] = useState('name');

  useEffect(() => {
    if (open && entityId) {
      loadData();
    } else {
      setAssignedNodeIds([]);
      setSelectedNodes([]);
      setError('');
      setSuccess('');
    }
  }, [open, entityId, entityType]);

  const loadData = async () => {
    if (!keycloak.token) return;

    try {
      setLoading(true);
      setError('');

      await keycloak.updateToken(30);

      const [nodesRes, assignedRes, currentEntityAssignedRes] = await Promise.all([
        loadAllNodes(),
        loadAllAssignedNodes(),
        loadAssignedNodes()
      ]);

      if (nodesRes.success) {
        const transformedNodes = nodesRes.nodes.map(node =>
          transformNodeToDisplayFormat(node) || node
        ).filter(node => node !== null);

        setAvailableNodes(transformedNodes);

        // Use all assigned nodes to filter available nodes, but use current entity assigned nodes for display
        if (assignedRes.success && currentEntityAssignedRes.success) {
          const allAssigned = assignedRes.data?.nodeIds || [];
          const currentEntityAssigned = currentEntityAssignedRes.data?.nodeIds || [];

          // Store current entity assigned nodes for display in "Assigned Nodes" section
          setAssignedNodeIds(currentEntityAssigned);
        }
      } else {
        setError(nodesRes.error || 'Failed to load nodes');
      }
    } catch (err) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadAllNodes = async () => {
    try {
      let allNodes = [];
      let page = 0;
      const pageSize = 100;
      let hasMore = true;

      while (hasMore) {
        const response = await getNodes({
          paginationParams: { page, page_size: pageSize },
          token: keycloak.token
        });

        if (response.success && response.data?.nodes) {
          allNodes = [...allNodes, ...response.data.nodes];
          
          const paginationResponse = response.data.paginationResponse || {};
          const totalPages = Math.ceil((paginationResponse.total || allNodes.length) / pageSize);
          
          if (page + 1 >= totalPages || response.data.nodes.length < pageSize) {
            hasMore = false;
          } else {
            page++;
          }
        } else {
          hasMore = false;
          if (page === 0) {
            return { success: false, error: response.error || 'Failed to load nodes' };
          }
        }
      }

      return { success: true, nodes: allNodes };
    } catch (err) {
      return { success: false, error: err.message || 'Failed to load nodes' };
    }
  };

  const loadAssignedNodes = async () => {
    // For the current entity, get its assigned nodes
    let currentEntityNodes = [];
    switch (entityType) {
      case 'client':
        const clientResult = await getClientNodes({ clientId: entityId, token: keycloak.token });
        if (clientResult.success) {
          currentEntityNodes = clientResult.data.nodeIds || [];
        }
        break;
      case 'department':
        const deptResult = await getDepartmentNodes({ departmentId: entityId, token: keycloak.token });
        if (deptResult.success) {
          currentEntityNodes = deptResult.data.nodeIds || [];
        }
        break;
      case 'team':
        const teamResult = await getTeamNodes({ teamId: entityId, token: keycloak.token });
        if (teamResult.success) {
          currentEntityNodes = teamResult.data.nodeIds || [];
        }
        break;
      case 'user':
        const userResult = await getUserNodes({ userId: entityId, token: keycloak.token });
        if (userResult.success) {
          currentEntityNodes = userResult.data.nodeIds || [];
        }
        break;
      default:
        return { success: false };
    }

    return { success: true, data: { nodeIds: currentEntityNodes } };
  };

  const loadAllAssignedNodes = async () => {
    const allAssignedNodeIds = new Set();

    try {
      // Load nodes assigned to all entities the user has access to
      const promises = [];

      // Always load from the current entity type
      promises.push(loadAssignedNodes());

      // If user has access to other entity types, load those too
      if (isSuperAdmin || userPermissions?.hasClientAccess) {
        // Load all client nodes the user has access to
        if (userPermissions?.clientIds?.length > 0) {
          userPermissions.clientIds.forEach(clientId => {
            promises.push(getClientNodes({ clientId, token: keycloak.token }));
          });
        }
      }

      if (isSuperAdmin || userPermissions?.hasDepartmentAccess) {
        // Load all department nodes the user has access to
        if (userPermissions?.departmentIds?.length > 0) {
          userPermissions.departmentIds.forEach(deptId => {
            promises.push(getDepartmentNodes({ departmentId: deptId, token: keycloak.token }));
          });
        }
      }

      if (isSuperAdmin || userPermissions?.hasTeamAccess) {
        // Load all team nodes the user has access to
        if (userPermissions?.teamIds?.length > 0) {
          userPermissions.teamIds.forEach(teamId => {
            promises.push(getTeamNodes({ teamId, token: keycloak.token }));
          });
        }
      }

      const results = await Promise.allSettled(promises);

      // Collect all assigned node IDs
      results.forEach(result => {
        if (result.status === 'fulfilled' && result.value.success) {
          const nodeIds = result.value.data?.nodeIds || [];
          nodeIds.forEach(nodeId => allAssignedNodeIds.add(nodeId));
        }
      });

      return { success: true, data: { nodeIds: Array.from(allAssignedNodeIds) } };
    } catch (error) {
      console.error('Error loading all assigned nodes:', error);
      // Fallback to just current entity nodes
      return await loadAssignedNodes();
    }
  };

  const handleAssign = async () => {
    if (selectedNodes.length === 0) {
      setError('Please select at least one node');
      return;
    }

    try {
      setAssigning(true);
      setError('');
      setSuccess('');

      await keycloak.updateToken(30);

      const nodeIds = selectedNodes.map(n => n.id || n.nodeId);
      const errors = [];

      for (const nodeId of nodeIds) {
        try {
          let result;
          switch (entityType) {
            case 'client':
              result = await assignNodeToClient({
                nodeId,
                clientId: entityId,
                token: keycloak.token
              });
              break;
            case 'department':
              result = await assignNodeToDepartments({
                nodeId,
                departmentIds: [entityId],
                token: keycloak.token
              });
              break;
            case 'team':
              result = await assignNodeToTeams({
                nodeId,
                teamIds: [entityId],
                token: keycloak.token
              });
              break;
            case 'user':
              result = await assignNodeToUsers({
                nodeId,
                userIds: [entityId],
                token: keycloak.token
              });
              break;
            default:
              throw new Error('Invalid entity type');
          }

          if (!result.success) {
            errors.push(`${nodeId}: ${result.error || 'Failed'}`);
          }
        } catch (err) {
          errors.push(`${nodeId}: ${err.message}`);
        }
      }

      if (errors.length === 0) {
        setSuccess(`${nodeIds.length} node${nodeIds.length !== 1 ? 's' : ''} assigned successfully`);
        setSelectedNodes([]);
        await loadData();
        if (onSuccess) onSuccess();
      } else if (errors.length < nodeIds.length) {
        setSuccess(`${nodeIds.length - errors.length} node${nodeIds.length - errors.length !== 1 ? 's' : ''} assigned, ${errors.length} failed`);
        setError(errors.join('; '));
        await loadData();
        if (onSuccess) onSuccess();
      } else {
        setError(`Failed to assign nodes: ${errors.join('; ')}`);
      }
    } catch (err) {
      setError(err.message || 'Failed to assign node');
    } finally {
      setAssigning(false);
    }
  };

  const handleRemove = async (nodeId) => {
    try {
      setAssigning(true);
      setError('');
      setSuccess('');

      await keycloak.updateToken(30);

      let result;
      switch (entityType) {
        case 'client':
          result = await removeNodeFromClient({
            nodeId,
            clientId: entityId,
            token: keycloak.token
          });
          break;
        case 'department':
          result = await removeNodeFromDepartment({
            nodeId,
            departmentId: entityId,
            token: keycloak.token
          });
          break;
        case 'team':
          result = await removeNodeFromTeam({
            nodeId,
            teamId: entityId,
            token: keycloak.token
          });
          break;
        case 'user':
          result = await removeNodeFromUser({
            nodeId,
            userId: entityId,
            token: keycloak.token
          });
          break;
        default:
          throw new Error('Invalid entity type');
      }

      if (result.success) {
        setSuccess('Node removed successfully');
        await loadData();
        if (onSuccess) onSuccess();
      } else {
        setError(result.error || 'Failed to remove node');
      }
    } catch (err) {
      setError(err.message || 'Failed to remove node');
    } finally {
      setAssigning(false);
    }
  };

  const assignedNodes = availableNodes.filter(node =>
    assignedNodeIds.includes(node.id || node.nodeId)
  );

  const unassignedNodes = availableNodes.filter(node =>
    !assignedNodeIds.includes(node.id || node.nodeId)
  );

  const sortedUnassignedNodes = useMemo(() => {
    return [...unassignedNodes].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          const nameA = (a.hostname || a.name || '').toLowerCase();
          const nameB = (b.hostname || b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);

        case 'gpu-vendor':
          const getPrimaryGpuVendor = (node) => {
            if (node.gpuBreakdown && node.gpuBreakdown.length > 0) {
              const gpu = node.gpuBreakdown[0];
              const model = gpu.model.toLowerCase();
              if (model.includes('rtx') || model.includes('gtx') || model.includes('tesla') || model.includes('quadro') || model.includes('geforce')) {
                return 'nvidia';
              } else if (model.includes('radeon') || model.includes('rx ')) {
                return 'amd';
              } else if (model.includes('intel') || model.includes('arc')) {
                return 'intel';
              }
            }
            return 'unknown';
          };

          const vendorA = getPrimaryGpuVendor(a);
          const vendorB = getPrimaryGpuVendor(b);
          if (vendorA !== vendorB) {
            const vendorOrder = { nvidia: 0, amd: 1, intel: 2, unknown: 3 };
            return vendorOrder[vendorA] - vendorOrder[vendorB];
          }
          // If same vendor, sort by name
          return (a.hostname || a.name || '').toLowerCase().localeCompare((b.hostname || b.name || '').toLowerCase());

        case 'gpu-count':
          const getTotalGpuCount = (node) => {
            if (node.gpuBreakdown && node.gpuBreakdown.length > 0) {
              return node.gpuBreakdown.reduce((sum, gpu) => sum + gpu.count, 0);
            }
            return node.numGpus || 0;
          };

          const gpuCountA = getTotalGpuCount(a);
          const gpuCountB = getTotalGpuCount(b);
          if (gpuCountB !== gpuCountA) {
            return gpuCountB - gpuCountA; // Higher GPU count first
          }
          // If same count, sort by name
          return (a.hostname || a.name || '').toLowerCase().localeCompare((b.hostname || b.name || '').toLowerCase());

        case 'cpu':
          const cpuA = a.numOfCpus || 0;
          const cpuB = b.numOfCpus || 0;
          if (cpuB !== cpuA) {
            return cpuB - cpuA; // Higher CPU count first
          }
          return (a.hostname || a.name || '').toLowerCase().localeCompare((b.hostname || b.name || '').toLowerCase());

        case 'memory':
          const memA = a.mem || 0;
          const memB = b.mem || 0;
          if (memB !== memA) {
            return memB - memA; // Higher memory first
          }
          return (a.hostname || a.name || '').toLowerCase().localeCompare((b.hostname || b.name || '').toLowerCase());

        case 'storage':
          const storageA = a.storage || 0;
          const storageB = b.storage || 0;
          if (storageB !== storageA) {
            return storageB - storageA; // Higher storage first
          }
          return (a.hostname || a.name || '').toLowerCase().localeCompare((b.hostname || b.name || '').toLowerCase());

        case 'region':
          const regionA = (a.region || '').toLowerCase();
          const regionB = (b.region || '').toLowerCase();
          if (regionA !== regionB) {
            return regionA.localeCompare(regionB);
          }
          return (a.hostname || a.name || '').toLowerCase().localeCompare((b.hostname || b.name || '').toLowerCase());

        default:
          return (a.hostname || a.name || '').toLowerCase().localeCompare((b.hostname || b.name || '').toLowerCase());
      }
    });
  }, [unassignedNodes, sortBy]);

  const getEntityLabel = () => {
    switch (entityType) {
      case 'client': return 'Client';
      case 'department': return 'Department';
      case 'team': return 'Team';
      case 'user': return 'User';
      default: return 'Entity';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight={600}>
            Manage Nodes - {entityName}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            {error && (
              <Alert severity="error" onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" onClose={() => setSuccess('')}>
                {success}
              </Alert>
            )}

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Assign New Nodes ({sortedUnassignedNodes.length} available)
                </Typography>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Sort by</InputLabel>
                  <Select
                    value={sortBy}
                    label="Sort by"
                    onChange={(e) => setSortBy(e.target.value)}
                    startAdornment={<SortIcon sx={{ mr: 1, color: 'action.active' }} />}
                  >
                    <MenuItem value="name">Name</MenuItem>
                    <MenuItem value="gpu-vendor">GPU Vendor</MenuItem>
                    <MenuItem value="gpu-count">GPU Count</MenuItem>
                    <MenuItem value="cpu">CPU Cores</MenuItem>
                    <MenuItem value="memory">Memory</MenuItem>
                    <MenuItem value="storage">Storage</MenuItem>
                    <MenuItem value="region">Region</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {selectedNodes.length > 0 && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: 'primary.light', borderRadius: 1, color: 'primary.contrastText' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {selectedNodes.length} node{selectedNodes.length !== 1 ? 's' : ''} selected
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAssign}
                    disabled={assigning}
                    size="small"
                    sx={{ mt: 1, bgcolor: 'primary.dark' }}
                  >
                    Assign Selected Node{selectedNodes.length !== 1 ? 's' : ''}
                  </Button>
                </Box>
              )}

              <Box sx={{
                maxHeight: 400,
                overflowY: 'auto',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 1
              }}>
                {sortedUnassignedNodes.length > 0 ? (
                  <Stack spacing={1}>
                    {sortedUnassignedNodes.map((node) => {
                      const isSelected = selectedNodes.some(selected =>
                        (selected.id || selected.nodeId) === (node.id || node.nodeId)
                      );

                      return (
                          <Card
                            sx={{
                              cursor: 'pointer',
                              border: 2,
                              borderColor: isSelected ? 'primary.main' : 'divider',
                              bgcolor: isSelected ? 'primary.light' : 'background.paper',
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                borderColor: 'primary.main',
                                transform: 'translateY(-1px)',
                                boxShadow: 2
                              }
                            }}
                            onClick={() => {
                              const nodeId = node.id || node.nodeId;
                              if (isSelected) {
                                setSelectedNodes(prev => prev.filter(selected =>
                                  (selected.id || selected.nodeId) !== nodeId
                                ));
                              } else {
                                setSelectedNodes(prev => [...prev, node]);
                              }
                            }}
                          >
                            <CardContent sx={{ p: 1.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Checkbox
                                  checked={isSelected}
                                  onChange={() => {}}
                                  sx={{ p: 0 }}
                                />

                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                                      {node.hostname || node.name || 'Unnamed Node'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {node.ipAddress || node.ip_address || 'N/A'}
                                    </Typography>
                                    {node.region && (
                                      <Chip
                                        label={node.region}
                                        size="small"
                                        variant="outlined"
                                        sx={{ height: 20, fontSize: '0.7rem' }}
                                      />
                                    )}
                                  </Box>

                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                                    {/* Hardware Resources Hierarchy */}
                                    {(formatGpuInfo(node) || (node.numOfCpus && node.numOfCpus > 0) || (node.mem && node.mem > 0) || (node.storage && node.storage > 0)) && (
                                      <Box>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.65rem', mb: 0.3 }}>
                                          RESOURCES
                                        </Typography>
                                        <Box sx={{ pl: 0.5 }}>
                                          {/* GPU info */}
                                          {formatGpuInfo(node) && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mb: 0.2 }}>
                                              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'success.main' }}>
                                                •
                                              </Typography>
                                              <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.65rem' }}>
                                                GPU:
                                              </Typography>
                                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                                {formatGpuInfo(node)}
                                              </Typography>
                                            </Box>
                                          )}

                                          {/* CPU info */}
                                          {node.numOfCpus && node.numOfCpus > 0 && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mb: 0.2 }}>
                                              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'success.main' }}>
                                                •
                                              </Typography>
                                              <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.65rem' }}>
                                                CPU:
                                              </Typography>
                                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                                {node.numOfCpus} cores
                                              </Typography>
                                            </Box>
                                          )}

                                          {/* Memory info */}
                                          {node.mem && node.mem > 0 && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mb: 0.2 }}>
                                              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'success.main' }}>
                                                •
                                              </Typography>
                                              <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.65rem' }}>
                                                RAM:
                                              </Typography>
                                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                                {Math.round(node.mem)}GB
                                              </Typography>
                                            </Box>
                                          )}

                                          {/* Storage info */}
                                          {node.storage && node.storage > 0 && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'success.main' }}>
                                                •
                                              </Typography>
                                              <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.65rem' }}>
                                                Disk:
                                              </Typography>
                                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                                {node.storage >= 1000 ? `${(node.storage / 1000).toFixed(1)}TB` : `${node.storage}GB`}
                                              </Typography>
                                            </Box>
                                          )}
                                        </Box>
                                      </Box>
                                    )}
                                  </Box>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                      );
                    })}
                  </Stack>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No available nodes to assign
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                Assigned Nodes ({assignedNodes.length})
              </Typography>
              {assignedNodes.length > 0 ? (
                <List dense>
                  {assignedNodes.map((node) => (
                    <ListItem
                      key={node.id || node.nodeId}
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: 'background.paper'
                      }}
                    >
                      <Computer sx={{ mr: 1.5, color: 'primary.main' }} />
                      <ListItemText
                        primary={node.hostname || node.name || 'Unnamed Node'}
                        secondary={
                          <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              {(node.ipAddress || node.ip_address) && (
                                <Typography variant="caption" component="span">
                                  {(node.ipAddress || node.ip_address)}
                                </Typography>
                              )}
                              {node.status && (
                                <Chip
                                  label={node.status}
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.65rem',
                                    bgcolor: node.status === 'Active' ? 'success.main' :
                                            node.status === 'Offline' ? 'error.main' : 'warning.main',
                                    color: 'white'
                                  }}
                                />
                              )}
                              {node.region && (
                                <Typography variant="caption" color="text.secondary" component="span">
                                  • {node.region}
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {/* Hardware Resources Hierarchy */}
                              {(formatGpuInfo(node) || (node.numOfCpus && node.numOfCpus > 0) || (node.mem && node.mem > 0) || (node.storage && node.storage > 0)) && (
                                <Box>
                                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.7rem' }}>
                                    HARDWARE RESOURCES
                                  </Typography>
                                  <Box sx={{ pl: 1, mt: 0.5 }}>
                                    {/* GPU info with detailed breakdown */}
                                    {formatGpuInfo(node) && (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                                        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'success.main' }}>
                                          ▶
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                                          GPU:
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                          {formatGpuInfo(node)}
                                        </Typography>
                                      </Box>
                                    )}
                                    {/* CPU info */}
                                    {node.numOfCpus && node.numOfCpus > 0 && (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                                        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'success.main' }}>
                                          ▶
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                                          CPU:
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                          {node.numOfCpus} cores
                                        </Typography>
                                      </Box>
                                    )}
                                    {/* Memory info */}
                                    {node.mem && node.mem > 0 && (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                                        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'success.main' }}>
                                          ▶
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                                          Memory:
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                          {Math.round(node.mem)}GB RAM
                                        </Typography>
                                      </Box>
                                    )}
                                    {/* Storage info */}
                                    {node.storage && node.storage > 0 && (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'success.main' }}>
                                          ▶
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                                          Storage:
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                          {node.storage >= 1000 ? `${(node.storage / 1000).toFixed(1)}TB` : `${node.storage}GB`}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          size="small"
                          color="error"
                          onClick={() => {
                            const nodeId = node.id || node.nodeId;
                            if (nodeId) handleRemove(nodeId);
                          }}
                          disabled={assigning}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  No nodes assigned to this {getEntityLabel().toLowerCase()}
                </Typography>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={assigning}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NodeAssignmentDialog;

